import React, { useEffect, useRef, useState, useCallback, RefObject, useMemo } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import jwtDecode from 'jwt-decode';

import { actions as accountActions } from '../../actions/accountActions';
import Routes from '../../constants/routes';
import Modal61 from '../Common/Modal/Modal61';

interface IdleTimerReduxProps {
	token?: string;
	accountActions: typeof accountActions;
}

interface Modal61Handle {
	show: () => void;
	hide: () => void;
}

type IdleTimerProps = IdleTimerReduxProps & RouteComponentProps;

function IdleTimer(props: IdleTimerProps) {
	const { token, accountActions, history } = props;

	// Instead of early-returning, we store skipMonitoring
	// but still let all Hooks run in the same order
	const currentPath = history.location.pathname;
	const skipMonitoring = useMemo(() => currentPath === Routes.LogoutPage, [currentPath]);

	// All Hooks still get called:
	const idleTimerRef: RefObject<Modal61Handle> = useRef<Modal61Handle>(null);

	// Just keep the countdown in state
	const [secondsRemaining, setSecondsRemaining] = useState<number>(60);

	// For token expiration
	const tokenEpochExpirationRef = useRef<number | null>(null);

	// Inactivity and countdown are stored in refs, so updating them
	// doesn't cause re-render
	const inactivityTimerIdRef = useRef<number | null>(null);
	const countdownIntervalIdRef = useRef<number | null>(null);
	const lastUserActivityRef = useRef<number>(Date.now());

	// decodeToken
	const decodeToken = useCallback(() => {
		if (!token) return;
		try {
			const decoded: { exp?: number } = jwtDecode(token);
			if (decoded?.exp) {
				const epochMs = decoded.exp * 1000;
				tokenEpochExpirationRef.current = epochMs;
				scheduleTokenRefresh(epochMs);
			}
		} catch (err) {
			console.error('Invalid token or decode failure:', err);
		}
	}, [token]);

	// scheduleTokenRefresh
	const scheduleTokenRefresh = useCallback((epochMs: number) => {
		const now = Date.now();
		const msRemaining = epochMs - now;
		// 30s after token expiration
		const refreshIn = msRemaining + 30_000;
		if (refreshIn > 0) {
			window.setTimeout(refreshToken, refreshIn);
		}
	}, []);

	// refreshToken
	const refreshToken = useCallback(() => {
		if (token) {
			accountActions.verify();
		}
	}, [token, accountActions]);

	// closeWarningModal
	const closeWarningModal = useCallback(() => {
		idleTimerRef.current?.hide();

		if (countdownIntervalIdRef.current) {
			clearInterval(countdownIntervalIdRef.current);
			countdownIntervalIdRef.current = null;
		}
		if (inactivityTimerIdRef.current) {
			clearTimeout(inactivityTimerIdRef.current);
			inactivityTimerIdRef.current = null;
		}

		// Mark as closed
		warningOpenRef.current = false;
	}, []);

	// logout
	const logout = useCallback(() => {
		closeWarningModal();
		history.push(Routes.LogoutPage);
	}, [closeWarningModal, history]);

	// This ref tracks whether we have displayed the warning already
	const warningOpenRef = useRef<boolean>(false);

	// showWarningModal
	const showWarningModal = useCallback(() => {
		// Only show if it's not open already
		if (skipMonitoring || warningOpenRef.current) return;

		const timeSinceLast = Date.now() - lastUserActivityRef.current;
		// If user idle <= 20 min
		if (token && timeSinceLast <= 20 * 60_000) {
			const countdownId = window.setInterval(() => {
				setSecondsRemaining((prev) => {
					const next = prev - 1;
					if (next <= 0) {
						logout();
						return 0;
					}
					return next;
				});
			}, 1000);

			countdownIntervalIdRef.current = countdownId;
			setSecondsRemaining(60);

			idleTimerRef.current?.show();
		}
	}, [token, logout, skipMonitoring]);

	// resetTimer
	const resetTimer = useCallback(
		(doReset = true) => {
			// We'll skip logic if skipMonitoring or no token
			if (skipMonitoring || !token || !doReset) return;

			// Clear old inactivity
			if (inactivityTimerIdRef.current) {
				clearTimeout(inactivityTimerIdRef.current);
			}

			// Record user activity
			lastUserActivityRef.current = Date.now();

			// After 10 minutes -> showWarningModal
			const newTimerId = window.setTimeout(showWarningModal, 10 * 60_000);
			inactivityTimerIdRef.current = newTimerId;
		},
		[skipMonitoring, token, showWarningModal]
	);

	// On mount
	useEffect(() => {
		// We'll always call these hooks in the same order,
		// but skip logic inside if skipMonitoring
		decodeToken();
		resetTimer();

		// Attach events once
		const handleActivity = () => resetTimer();

		window.onload = handleActivity;
		document.onclick = handleActivity;
		document.onkeyup = handleActivity;
		document.onmousemove = handleActivity;

		return () => {
			if (inactivityTimerIdRef.current) {
				clearTimeout(inactivityTimerIdRef.current);
			}
			if (countdownIntervalIdRef.current) {
				clearInterval(countdownIntervalIdRef.current);
			}
		};
	}, []);

	// Watch for token changes
	useEffect(() => {
		decodeToken();
		resetTimer(false);
	}, [token, decodeToken, resetTimer]);

	// Render everything
	// If skipping, we still "render" the same hooks, but we can empty the UI:
	if (skipMonitoring) {
		// We do not early return => to keep Hook order consistent
		return null;
	}

	const modalBody = <div>You have {secondsRemaining} seconds left before you are automatically logged out due to inactivity.</div>;

	return (
		<Modal61
			ref={idleTimerRef}
			title='Inactivity warning'
			body={modalBody}
			primaryButtonText='Stay logged in'
			onPrimaryClick={closeWarningModal}
			showSecondaryButton
			secondaryButtonText='Logout'
			onSecondaryClick={logout}
			onCloseClick={closeWarningModal}
			isCentered
			isLoading={false}
		/>
	);
}

interface RootState {
	user: {
		accessToken: string;
	};
}

const mapStateToProps = (state: RootState) => ({
	token: state.user.accessToken,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	accountActions: bindActionCreators({ ...accountActions }, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(IdleTimer));
